import { LinkWithIconProps } from 'wds/linkWithIcon';
import { BadgeTooltip } from 'wds/popoverTooltip';
import { i18nStore } from '~/modules/stores/i18nStore';
import { throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';
import { getUniqueIteratorKey } from '~/modules/utilities/lwcUtilities';

export type BadgeKey = keyof I18nMessages['listingDetails']['badges'];

export enum BadgeVariant {
    Blue = 'blue',
    Purple = 'purple',
    Orange = 'orange',
    HotOrange = 'hot-orange',
    Gray = 'gray',
    Fuchsia = 'fuchsia',
    Indigo = 'indigo',
    Green = 'green',
}

export interface SimpleBadgeProps {
    value: string;
    variant?: BadgeVariant;
    iteratorKey?: string;
}

export const BADGE_KEY_TO_VARIANT_MAP: Partial<Record<BadgeKey | 'Default', BadgeVariant>> = {
    isPledge1Percent: BadgeVariant.Indigo,
    isDiverseOwnedBusiness: BadgeVariant.Purple,
    salesforceMVP: BadgeVariant.Purple,
    topContributor: BadgeVariant.HotOrange,
    trailheadRank: BadgeVariant.Blue,
    Default: BadgeVariant.Gray,
};

export function generateSimpleBadge(value: string, variant?: BadgeVariant) {
    return {
        value: value || '',
        variant: variant || BADGE_KEY_TO_VARIANT_MAP.Default,
        iteratorKey: getUniqueIteratorKey(),
    };
}

export function generateLocalizedBadge(i18nKey: BadgeKey, i18nFn?: (args?: Record<string, string>) => string) {
    return {
        i18nFn: i18nFn ?? getBadgeI18nFunction(i18nKey),
        i18nKey,
        variant: BADGE_KEY_TO_VARIANT_MAP[i18nKey] || BADGE_KEY_TO_VARIANT_MAP.Default,
        iteratorKey: getUniqueIteratorKey(),
    };
}

export function getBadgeI18nFunction(badgeKey: BadgeKey): (args?: Record<string, string>) => string {
    const i18nFn = i18nStore.getState().i18n.listingDetails.badges[badgeKey];
    if (!i18nFn) {
        throwDevelopmentOrLog(
            new Error(`Failed to find i18n entry for badge '${badgeKey}' in i18n.listingDetails.badges.`),
        );
        return () => '';
    }
    return i18nFn;
}

type TooltipLinks = {
    isAccessibleSolution?: LinkWithIconProps;
    isDiverseOwnedBusiness?: LinkWithIconProps;
};

function generateTooltipLinks(extension?: schema.oaf.Force_Listing): TooltipLinks {
    const tooltipLinks: TooltipLinks = {};

    if (extension?.isAccessibleSolution) {
        const accessibleSolutionURL = extension.accessibleSolutionURL;
        if (!accessibleSolutionURL) {
            throwDevelopmentOrLog(new Error(`The URL for the accessible solution is missing in extension.`));
        }
        tooltipLinks.isAccessibleSolution = {
            // @ts-expect-error * Automated Type Error Fix
            url: accessibleSolutionURL,
            icon: 'new_window',
            caption: i18nStore.getState().i18nStaticCache.listingDetails.tooltipLinkCaptions.isAccessibleSolution,
        };
    }

    if (extension?.isDiverseOwnedBusiness) {
        const diverseOwnedBusinessURL = extension.diverseOwnedBusinessURL;
        if (!diverseOwnedBusinessURL) {
            throwDevelopmentOrLog(new Error(`The URL for the diverse owned business is missing in extension.`));
        }
        tooltipLinks.isDiverseOwnedBusiness = {
            // @ts-expect-error * Automated Type Error Fix
            url: diverseOwnedBusinessURL,
            icon: 'new_window',
            caption: i18nStore.getState().i18nStaticCache.listingDetails.tooltipLinkCaptions.isDiverseOwnedBusiness,
        };
    }

    return tooltipLinks;
}

/**
 * Generates the tooltip rows based on the given features array, extension, and tooltip messages.
 *
 * @param badgeKeys The array of badges keys to generate the tooltips for.
 * @param extension The listing extension containing the link URLs.
 * @returns An array of BadgeTooltip, one for each badge key.
 */
export function generateBadgeTooltips(badgeKeys: BadgeKey[], extension?: schema.oaf.Force_Listing): BadgeTooltip[] {
    const tooltipLinks = generateTooltipLinks(extension);

    return badgeKeys.map((key) => {
        // @ts-expect-error * Automated Type Error Fix
        const description = i18nStore.getState().i18nStaticCache.listingDetails.tooltipsText[key];
        if (!description) {
            throwDevelopmentOrLog(new Error(`The description for badge ${key} is missing in the i18nStaticCache.`));
        }
        // @ts-expect-error * Automated Type Error Fix
        const link = tooltipLinks[key];
        return {
            badge: generateLocalizedBadge(key),
            description: description ?? '',
            link,
        };
    });
}
